import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Map as MapIcon, Plus, Pencil, Trash2, Loader2, X, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { ROADMAP_ICON_KEYS, roadmapIcon } from '../lib/roadmapIcons';

type RoadmapItem = {
  id: string;
  title: string;
  description: string;
  icon: string;
  targetMonth: number | null;
  targetYear: number | null;
  status: 'planned' | 'in_progress' | 'shipped';
  sortOrder: number;
  published: boolean;
  votes: number;
};

const STATUS_LABEL: Record<string, string> = {
  planned: 'Planificado',
  in_progress: 'En desarrollo',
  shipped: 'Disponible',
};
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export function AdminRoadmap() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'roadmap'],
    queryFn: () => api<{ items: RoadmapItem[] }>('/api/admin/roadmap'),
  });
  const [editing, setEditing] = useState<RoadmapItem | null>(null);
  const [creating, setCreating] = useState(false);

  const items = data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MapIcon size={22} className="text-brand-600" /> Roadmap
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Entradas públicas del roadmap. Los visitantes pueden votar (1 voto por IP).
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary gap-2 shrink-0">
          <Plus size={16} /> Nueva entrada
        </button>
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center text-slate-300">
          <Loader2 size={28} className="animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm py-16 text-center text-sm text-slate-500">
          Aún no hay entradas. Crea la primera para que aparezca en la landing.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => {
            const Icon = roadmapIcon(item.icon);
            const when = item.targetMonth && item.targetYear
              ? `${MONTHS[item.targetMonth - 1]} ${item.targetYear}`
              : item.targetYear
                ? String(item.targetYear)
                : 'Sin fecha';
            return (
              <div key={item.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                  <Icon size={19} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 truncate">{item.title}</span>
                    {!item.published && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        <EyeOff size={10} /> Oculta
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{item.description || 'Sin descripción'}</p>
                  <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-400">
                    <span>{STATUS_LABEL[item.status]}</span>
                    <span>·</span>
                    <span>{when}</span>
                    <span>·</span>
                    <span className="inline-flex items-center gap-0.5 font-semibold text-slate-600">
                      <ChevronUp size={12} /> {item.votes} {item.votes === 1 ? 'voto' : 'votos'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setEditing(item)}
                  className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-brand-600 transition"
                  title="Editar"
                >
                  <Pencil size={15} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <RoadmapEditor
          item={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/* ── Editor modal (create + edit + delete) ──────────────── */
function RoadmapEditor({ item, onClose }: { item: RoadmapItem | null; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = Boolean(item);
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [icon, setIcon] = useState(item?.icon ?? 'Sparkles');
  const [month, setMonth] = useState<number | ''>(item?.targetMonth ?? '');
  const [year, setYear] = useState<number | ''>(item?.targetYear ?? '');
  const [status, setStatus] = useState<RoadmapItem['status']>(item?.status ?? 'planned');
  const [sortOrder, setSortOrder] = useState(item?.sortOrder ?? 0);
  const [published, setPublished] = useState(item?.published ?? true);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title: title.trim(),
        description: description.trim(),
        icon,
        targetMonth: month === '' ? null : Number(month),
        targetYear: year === '' ? null : Number(year),
        status,
        sortOrder: Number(sortOrder) || 0,
        published,
      };
      return api(`/api/admin/roadmap${isEdit ? `/${item!.id}` : ''}`, {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roadmap'] });
      onClose();
    },
    onError: () => setError('No se pudo guardar. Revisa los campos.'),
  });

  const del = useMutation({
    mutationFn: () => api(`/api/admin/roadmap/${item!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'roadmap'] });
      onClose();
    },
  });

  const currentYear = new Date().getFullYear();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 sticky top-0 bg-white">
          <h2 className="font-semibold text-slate-900">{isEdit ? 'Editar entrada' : 'Nueva entrada'}</h2>
          <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-600">Título</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Ej. Bandeja unificada de mensajes"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600">Descripción</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={5000}
              rows={3}
              placeholder="Qué resuelve esta feature, en una o dos frases."
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
            />
          </div>

          {/* Icon picker */}
          <div>
            <label className="text-xs font-semibold text-slate-600">Icono</label>
            <div className="mt-1.5 grid grid-cols-8 gap-1.5">
              {ROADMAP_ICON_KEYS.map((key) => {
                const Ico = roadmapIcon(key);
                const active = key === icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setIcon(key)}
                    title={key}
                    className={cn(
                      'aspect-square inline-flex items-center justify-center rounded-lg border transition',
                      active
                        ? 'border-brand-400 bg-brand-50 text-brand-700 ring-2 ring-brand-200'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50',
                    )}
                  >
                    <Ico size={17} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Target date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Mes (aprox.)</label>
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value === '' ? '' : Number(e.target.value))}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
              >
                <option value="">—</option>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Año (aprox.)</label>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value === '' ? '' : Number(e.target.value))}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
              >
                <option value="">—</option>
                {[0, 1, 2, 3].map((d) => (
                  <option key={d} value={currentYear + d}>{currentYear + d}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Status + order */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Estado</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as RoadmapItem['status'])}
                className="mt-1 w-full rounded-lg border border-slate-200 px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-200"
              >
                <option value="planned">Planificado</option>
                <option value="in_progress">En desarrollo</option>
                <option value="shipped">Disponible</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Orden</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value))}
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200"
              />
            </div>
          </div>

          {/* Published toggle */}
          <button
            type="button"
            onClick={() => setPublished((v) => !v)}
            className={cn(
              'inline-flex items-center gap-2 text-sm font-medium rounded-lg px-3 py-2 border transition',
              published
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : 'bg-slate-50 text-slate-500 border-slate-200',
            )}
          >
            {published ? <Eye size={15} /> : <EyeOff size={15} />}
            {published ? 'Visible en la landing' : 'Oculta'}
          </button>

          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 sticky bottom-0 bg-white">
          {isEdit ? (
            <button
              onClick={() => {
                if (confirm('¿Eliminar esta entrada del roadmap? Se borrarán también sus votos.')) del.mutate();
              }}
              disabled={del.isPending}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 rounded-lg px-3 py-1.5 transition"
            >
              <Trash2 size={15} /> Eliminar
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => {
              setError(null);
              if (!title.trim()) {
                setError('El título es obligatorio.');
                return;
              }
              save.mutate();
            }}
            disabled={save.isPending}
            className="btn-primary gap-2"
          >
            {save.isPending && <Loader2 size={15} className="animate-spin" />}
            {isEdit ? 'Guardar cambios' : 'Crear entrada'}
          </button>
        </div>
      </div>
    </div>
  );
}
